import { useEffect, useRef, useState } from 'react'
import {
  normalizeMediaRecoveryError,
  type MediaRecoveryState,
} from '../media-recovery'

function hasMediaRecorderSupport() {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  )
}

export function useAudioRecorder({
  maxDurationMs = 15000,
}: {
  readonly maxDurationMs?: number
}) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [recovery, setRecovery] = useState<MediaRecoveryState | null>(null)
  const [isRecording, setIsRecording] = useState(false)

  const clearAudioUrl = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
    }

    setAudioUrl(null)
  }

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop()
    })
    streamRef.current = null
  }

  const stopRecording = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }

  const startRecording = async () => {
    if (!hasMediaRecorderSupport()) {
      setRecovery(normalizeMediaRecoveryError('not supported', 'AUDIO_RECORDING'))
      return false
    }

    clearAudioUrl()
    setRecovery(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)

      streamRef.current = stream
      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }
      recorder.onerror = () => {
        setRecovery(normalizeMediaRecoveryError('recording failed', 'AUDIO_RECORDING'))
        setIsRecording(false)
        cleanupStream()
      }
      recorder.onstop = () => {
        const nextBlob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        })

        if (nextBlob.size > 0) {
          setAudioUrl(URL.createObjectURL(nextBlob))
        }

        setIsRecording(false)
        cleanupStream()
      }

      recorder.start()
      setIsRecording(true)

      timeoutRef.current = window.setTimeout(() => {
        stopRecording()
      }, maxDurationMs)

      return true
    } catch (error) {
      setRecovery(normalizeMediaRecoveryError(error, 'AUDIO_RECORDING'))
      cleanupStream()
      return false
    }
  }

  useEffect(
    () => () => {
      stopRecording()
      cleanupStream()

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    },
    [audioUrl],
  )

  return {
    audioUrl,
    clearAudioUrl,
    errorCode: recovery?.code ?? null,
    errorMessage: recovery?.message ?? null,
    isRecording,
    isSupported: hasMediaRecorderSupport(),
    startRecording,
    stopRecording,
  }
}
